import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import jsonExport from "jsonexport/dist";
import * as XLSX from "xlsx";
import { HiOutlineDownload, HiOutlineSearch } from "react-icons/hi";
import { useListContext, downloadCSV, useNotify } from "react-admin";
import { determineUserReadableRole } from "./UserUtils";
import { displayDateOnly } from "../../util/dateOnly";
import { colors, radius, shadows } from "../../data/tokens";

const columnNamesMapping: any = {
  id: "ID",
  createdAt: "Criado em",
  updatedAt: "Atualizado em",
  active: "Ativo",
  // Campos que antes apareciam com o nome cru do banco:
  cnpj: "CNPJ",
  CREA: "CREA",
  iuguId: "ID Iugu",
  login: "Login",
  role: "Regra de Usuário (nome)",
  avatar: "Foto de Perfil",
  subscription: "Assinatura",
  transactions: "Transações",
  technicalDivisionsHistory: "Histórico de Divisões Técnicas",
  dependentBirthDate: "Nascimento do Dependente",
  mailName: "Nome (busca)",
  // registrationNumberDigit: "Dígito Matrícula",
  registrationNumberProv: "Matrícula Provisória",
  userType: "Tipo de Usuário",
  userCategory: "Categoria",
  userCategoryId: "ID Categoria",
  hasPhysicalCard: "Possui Cartão Físico",
  physicalCardNumber: "Número Cartão Físico",
  physicalCardPicture: "Foto Cartão Físico",
  accountStatus: "Status Conta",
  // relative: "Parentesco",
  roleId: "Regra de Usuário",
  subscriptionStatus: "Status Assinatura",
  commercialEmail: "E-mail Comercial",
  commercialPhoneNumber: "Telefone Comercial",
  commercialAddress: "Endereço Comercial",
  commercialAddressNumber: "Número Comercial",
  commercialAddressComplement: "Complemento Comercial",
  commercialAddressNeighborhood: "Bairro Comercial",
  commercialAddressCountry: "País Comercial",
  commercialAddressState: "Estado Comercial",
  commercialAddressRegion: "Região Comercial",
  commercialAddressCity: "Cidade Comercial",
  commercialAddressZipCode: "CEP Comercial",
  userGraduations: "Graduações",
  graduationName1: "Graduação 1 - Nome",
  graduationInstitution1: "Graduação 1 - Inst. de Ensino",
  graduationYear1: "Graduação 1 - Ano",
  graduationName2: "Graduação 2 - Nome",
  graduationInstitution2: "Graduação 2 - Inst. de Ensino",
  graduationYear2: "Graduação 2 - Ano",
  graduationName3: "Graduação 3 - Nome",
  graduationInstitution3: "Graduação 3 - Inst. de Ensino",
  graduationYear3: "Graduação 3 - Ano",
  graduationName4: "Graduação 4 - Nome",
  graduationInstitution4: "Graduação 4 - Inst. de Ensino",
  graduationYear4: "Graduação 4 - Ano",
  graduationName5: "Graduação 5 - Nome",
  graduationInstitution5: "Graduação 5 - Inst. de Ensino",
  graduationYear5: "Graduação 5 - Ano",

  userElections: "Eleições",
  hasCREA: "Possui CREA",
  fullName: "Nome Completo",
  email: "E-mail",
  cpf: "CPF",
  residencePhoneNumber: "Telefone Residencial",
  registrationNumber: "Matrícula",
  birthDate: "Nascimento",
  positionRoles: "Cargos",
  professionalActivity: "Atividade Profissional",
  addressType: "Tipo de Endereço",
  address: "Endereço",
  addressNumber: "Número",
  addressState: "Estado",
  addressZipCode: "CEP",
  addressComplement: "Complemento",
  addressNeighborhood: "Bairro",
  addressCountry: "País",
  addressRegion: "Região",
  addressCity: "Cidade",
  maritalStatus: "Estado Civil",
  dependents: "Dependentes",
  dependent1: "Dependente 1",
  dependent2: "Dependente 2",
  dependent3: "Dependente 3",
  dependent4: "Dependente 4",
  dependent5: "Dependente 5",
  gender: "Gênero",
  phoneNumber: "Celular",
  cancellationDate: "Data de Cancelamento",
  deceasedDate: "Data de Óbito",
  licenseDate: "Data de Licenciado",
  admissionDate: "Data de Admissão",
  aspirantDate: "Data de Aspirante",
  effectiveDate: "Data de Efetivo",
  remissionDate: "Data de Remido",
  contributionDate: "Data de Contribuinte",
  collectiveDate: "Data de Coletivo",
  collectiveRemissionDate: "Data de Coletivo Remido",
  seniorDate: "Data de Sênior",
  technicalDivisions: "Divisões Técnicas",
  observation: "Observação",
  userEnabled: "Adimplência",
  userLastContribution: "Última Contribuição",
  // educationalInstitution: "Instituição de Ensino",
};

// Campos internos: não fazem sentido em uma planilha para o cliente.
const HIDDEN_COLUMNS = new Set([
  "password",
  "recoveryToken",
  "avatarId",
  "avatar",
  "emailValid",
  "relative",
  "registrationNumberDigit",
  "documents",
  "auditLogs",
  "roleId",
  "userCategoryId",
]);

// Colunas agrupadas por assunto, para o operador achar o campo pelo tema
// em vez de caçar na ordem crua do banco. O que não estiver em nenhum
// grupo aparece em "Outros", na ordem original.
const COLUMN_GROUPS: { title: string; columns: string[] }[] = [
  {
    title: "Identificação",
    columns: [
      "id",
      "fullName",
      "cpf",
      "cnpj",
      "CREA",
      "registrationNumber",
      "registrationNumberProv",
      "birthDate",
      "gender",
      "maritalStatus",
      "userType",
      "userCategory",
      "accountStatus",
      "subscriptionStatus",
    ],
  },
  {
    title: "Contato",
    columns: [
      "email",
      "commercialEmail",
      "phoneNumber",
      "residencePhoneNumber",
      "commercialPhoneNumber",
    ],
  },
  {
    title: "Endereço residencial",
    columns: [
      "addressType",
      "address",
      "addressNumber",
      "addressComplement",
      "addressNeighborhood",
      "addressCity",
      "addressState",
      "addressRegion",
      "addressCountry",
      "addressZipCode",
    ],
  },
  {
    title: "Endereço comercial",
    columns: [
      "commercialAddress",
      "commercialAddressNumber",
      "commercialAddressComplement",
      "commercialAddressNeighborhood",
      "commercialAddressCity",
      "commercialAddressState",
      "commercialAddressRegion",
      "commercialAddressCountry",
      "commercialAddressZipCode",
    ],
  },
  {
    title: "Datas de situação",
    columns: [
      "admissionDate",
      "aspirantDate",
      "effectiveDate",
      "contributionDate",
      "collectiveDate",
      "collectiveRemissionDate",
      "remissionDate",
      "seniorDate",
      "licenseDate",
      "cancellationDate",
      "deceasedDate",
    ],
  },
  {
    title: "Vida associativa",
    columns: [
      "professionalActivity",
      "technicalDivisions",
      "userGraduations",
      "positionRoles",
      "userElections",
      "dependents",
      "dependentBirthDate",
      "observation",
    ],
  },
  {
    title: "Sistema",
    columns: [
      "active",
      "userEnabled",
      "userLastContribution",
      "hasPhysicalCard",
      "physicalCardNumber",
      "physicalCardPicture",
      "iuguId",
      "login",
      "createdAt",
      "updatedAt",
    ],
  },
];

const GROUPED_COLUMNS = new Set(COLUMN_GROUPS.flatMap((g) => g.columns));

const columnLabel = (column: string) => columnNamesMapping[column] || column;

// Busca sem diferenciar acento nem maiúscula ("celular" acha "Celular")
const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// A Matrícula sai junto com o dígito verificador (coluna oculta) — mesma
// regra de antes, aplicada a qualquer forma de marcar
const withLinkedColumns = (list: string[]) => {
  const set = new Set(list);
  if (set.has("registrationNumber")) set.add("registrationNumberDigit");
  else set.delete("registrationNumberDigit");
  return Array.from(set);
};

const sectionTitleSx = {
  fontSize: 11.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: colors.textMuted,
};

// Modal no padrão do painel (mesmo desenho das Operações em massa):
// cartão com cabeçalho fixo e ações no topo; colunas agrupadas por
// assunto, com busca, "marcar todas" e marcar/desmarcar por grupo. O
// texto de cada coluna é clicável (antes só a caixinha). Durante a
// geração da planilha o modal fica aberto, com o andamento no botão.
export const ExportColumnSelectionDialog = ({
  onClose,
  onConfirm,
  columns,
  selectedColumns,
  exportFormat,
  blockedReason,
}: any) => {
  const [selected, setSelected] = useState<string[]>(
    withLinkedColumns(selectedColumns || [])
  );
  const [selectedExportFormat, setSelectedExportFormat] =
    useState(exportFormat); // csv or xlsx
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const visibleColumns: string[] = useMemo(
    () => (columns || []).filter((column: string) => !HIDDEN_COLUMNS.has(column)),
    [columns]
  );

  const groups = useMemo(() => {
    const available = new Set(visibleColumns);
    const list = COLUMN_GROUPS.map((group) => ({
      title: group.title,
      columns: group.columns.filter((column) => available.has(column)),
    }));
    const others = visibleColumns.filter((column) => !GROUPED_COLUMNS.has(column));
    if (others.length > 0) list.push({ title: "Outros", columns: others });
    const query = normalize(search.trim());
    return list
      .map((group) => ({
        ...group,
        columns: query
          ? group.columns.filter((column) =>
              normalize(columnLabel(column)).includes(query)
            )
          : group.columns,
      }))
      .filter((group) => group.columns.length > 0);
  }, [visibleColumns, search]);

  const selectedCount = visibleColumns.filter((column) =>
    selected.includes(column)
  ).length;

  // Toda mudança vale na hora e fica lembrada para a próxima exportação
  const updateSelection = (list: string[]) => {
    const next = withLinkedColumns(list);
    setSelected(next);
    localStorage.setItem("selectedColumns", JSON.stringify(next));
  };

  const toggleColumn = (column: string) =>
    updateSelection(
      selected.includes(column)
        ? selected.filter((c) => c !== column)
        : [...selected, column]
    );

  const toggleGroup = (groupColumns: string[]) => {
    const allSelected = groupColumns.every((c) => selected.includes(c));
    updateSelection(
      allSelected
        ? selected.filter((c) => !groupColumns.includes(c))
        : [...selected, ...groupColumns.filter((c) => !selected.includes(c))]
    );
  };

  const handleConfirm = async () => {
    setExporting(true);
    const done = await onConfirm(selected, selectedExportFormat);
    setExporting(false);
    // Em caso de falha o modal fica aberto, com as escolhas, para tentar de novo
    if (done !== false) onClose();
  };

  const requestClose = () => {
    if (!exporting) onClose();
  };

  const exportDisabled = exporting || selectedCount === 0 || Boolean(blockedReason);

  return (
    <Modal
      aria-labelledby="export-modal-title"
      open
      onClose={(_event, reason) => {
        // Clique fora não fecha (um clique acidental perdia as escolhas);
        // fecha pelo "Cancelar" ou pelo Esc — nunca durante a geração
        if (reason === "backdropClick") return;
        requestClose();
      }}
      closeAfterTransition
    >
      <Fade in>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
            pointerEvents: "none",
          }}
        >
          <Card
            sx={{
              pointerEvents: "auto",
              width: "min(980px, 96vw)",
              maxHeight: "88vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: `${radius.lg}px`,
              boxShadow: shadows.popover,
            }}
          >
            {/* Cabeçalho fixo: título, formato e ações sempre visíveis */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                padding: "16px 24px",
                borderBottom: `1px solid ${colors.border}`,
                flexShrink: 0,
              }}
            >
              <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                <Typography
                  id="export-modal-title"
                  sx={{ fontSize: 18, fontWeight: 700, color: colors.textStrong }}
                >
                  Exportar sócios
                </Typography>
                <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                  {selectedCount === 1
                    ? "1 coluna selecionada"
                    : `${selectedCount} colunas selecionadas`}{" "}
                  de {visibleColumns.length} · respeita os filtros aplicados na
                  lista
                </Typography>
              </Box>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={selectedExportFormat}
                onChange={(_e, value) => value && setSelectedExportFormat(value)}
                disabled={exporting}
                aria-label="Formato do arquivo"
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontSize: 13,
                    paddingX: 1.5,
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    color: colors.primary,
                    backgroundColor: colors.bgContent,
                    fontWeight: 600,
                  },
                }}
              >
                <ToggleButton value="xlsx">Excel (.xlsx)</ToggleButton>
                <ToggleButton value="csv">CSV (.csv)</ToggleButton>
              </ToggleButtonGroup>
              <Button
                size="small"
                onClick={requestClose}
                disabled={exporting}
                sx={{ color: colors.textMuted }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={exportDisabled}
                startIcon={
                  exporting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <HiOutlineDownload size={18} />
                  )
                }
              >
                {exporting ? "Gerando planilha…" : "Exportar"}
              </Button>
            </Box>

            {/* Busca e seleção rápida */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                padding: "12px 24px",
                borderBottom: `1px solid ${colors.border}`,
                backgroundColor: colors.bgSubtle,
                flexShrink: 0,
              }}
            >
              {/* hiddenLabel: sem rótulo, a variante "filled" do tema
                  reservava o espaço dele e empurrava texto e lupa para baixo */}
              <TextField
                size="small"
                hiddenLabel
                placeholder="Buscar coluna…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HiOutlineSearch size={16} color={colors.textMuted} />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: "1 1 220px", maxWidth: 360 }}
              />
              <Box sx={{ marginLeft: "auto", display: "flex", gap: 0.5 }}>
                <Button
                  size="small"
                  disabled={exporting || selectedCount === visibleColumns.length}
                  onClick={() => updateSelection([...selected, ...visibleColumns])}
                >
                  Marcar todas
                </Button>
                <Button
                  size="small"
                  sx={{ color: colors.textMuted }}
                  disabled={exporting || selectedCount === 0}
                  onClick={() => updateSelection([])}
                >
                  Limpar
                </Button>
              </Box>
            </Box>

            {/* Corpo com rolagem própria */}
            <Box sx={{ overflowY: "auto", padding: "8px 24px 20px" }}>
              {blockedReason && (
                <Alert severity="info" sx={{ marginTop: 1.5 }}>
                  {blockedReason}
                </Alert>
              )}
              {groups.length === 0 && (
                <Typography
                  sx={{ fontSize: 13.5, color: colors.textMuted, paddingY: 3 }}
                >
                  {visibleColumns.length === 0
                    ? "Nenhuma coluna disponível — a lista está vazia."
                    : "Nenhuma coluna encontrada para a busca."}
                </Typography>
              )}
              {groups.map((group) => {
                const groupSelected = group.columns.filter((c) =>
                  selected.includes(c)
                ).length;
                return (
                  <Box key={group.title} sx={{ marginTop: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        borderBottom: `1px solid ${colors.neutral200}`,
                        paddingBottom: 0.5,
                        marginBottom: 0.5,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={groupSelected === group.columns.length}
                        indeterminate={
                          groupSelected > 0 && groupSelected < group.columns.length
                        }
                        onChange={() => toggleGroup(group.columns)}
                        disabled={exporting}
                        inputProps={{
                          "aria-label": `Marcar ou desmarcar ${group.title}`,
                        }}
                      />
                      <Typography sx={sectionTitleSx}>{group.title}</Typography>
                      <Typography
                        sx={{ fontSize: 12, color: colors.textMuted, marginLeft: 1 }}
                      >
                        {groupSelected}/{group.columns.length}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(3, minmax(0, 1fr))",
                        },
                        columnGap: 2,
                      }}
                    >
                      {group.columns.map((column) => (
                        <FormControlLabel
                          key={column}
                          disabled={exporting}
                          control={
                            <Checkbox
                              size="small"
                              checked={selected.includes(column)}
                              onChange={() => toggleColumn(column)}
                            />
                          }
                          label={columnLabel(column)}
                          sx={{
                            marginLeft: 0,
                            marginRight: 0,
                            borderRadius: `${radius.sm}px`,
                            "&:hover": { backgroundColor: colors.bgContent },
                            "& .MuiFormControlLabel-label": {
                              fontSize: 13.5,
                              color: colors.textStrong,
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Card>
        </Box>
      </Fade>
    </Modal>
  );
};

// Nome do arquivo com a data (sem sobrescrever "users (1).xlsx")
const exportFileName = () => {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `socios-${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(
    today.getDate()
  )}`;
};

export const CustomExportButton = (props: any) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data, filterValues } = useListContext();
  const userRole = localStorage.getItem("roles");
  const isMobile = localStorage.getItem("isMobile") === "true";
  // filterValues is an object such as { mailName: "tatiana da silva", hasRegistrationNumber: "false" }
  let columns = data ? Object.keys(data[0] || {}) : [];
  const commsColumns = columns.filter(
    (column: any) =>
      column === "commercialEmail" ||
      column === "fullName" ||
      column === "email" ||
      column === "phoneNumber"
  );
  if (determineUserReadableRole(userRole) === "comms")
    columns = columns.filter((column: any) => commsColumns.includes(column));
  const notify = useNotify();
  // Mesma condição que já bloqueava a exportação (ver abaixo) — agora
  // avisada no modal, antes do clique
  const noFilterApplied =
    !props.location.search && Object.keys(filterValues || {}).length === 0;
  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  // Devolve true quando a planilha foi gerada (o modal fecha) e false
  // quando não foi (o modal continua aberto para tentar de novo)
  const handleColumnSelectionConfirm = async (
    selectedColumns: any,
    exportFormat: "xlsx" | "csv"
  ): Promise<boolean> => {
    let searchParams = props.location.search; // ?displayedFilters=%7B%7D&filter=%7B%22mailName%22%3A%22tatiana%20da%20silva%22%7D&order=ASC&page=1&perPage=10&sort=id
    if (!searchParams) {
      // if filterValues is not empty
      if (Object.keys(filterValues).length > 0) {
        searchParams = "?filter=" + JSON.stringify(filterValues);
      } else {
        notify("Nenhum filtro selecionado");
        return false;
      }
    }
    const urlParams = new URLSearchParams(searchParams);
    const filters = urlParams.get("filter");
    const commaColumns = selectedColumns.join(",");
    const auth = localStorage.getItem("auth");
    const authJson = auth ? JSON.parse(auth) : auth;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/users/export/${commaColumns}/?filter=${filters}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + authJson.access_token,
          },
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      let data = await response.json();
      if (!Array.isArray(data)) throw new Error("Resposta inesperada");

      // Datas civis (sem hora): precisam ser lidas em UTC, senão a
      // conversão para o fuso local recua um dia na planilha.
      const dateOnlyFields = [
        "cancellationDate",
        "deceasedDate",
        "licenseDate",
        "admissionDate",
        "aspirantDate",
        "effectiveDate",
        "remissionDate",
        "contributionDate",
        "collectiveDate",
        "collectiveRemissionDate",
        "seniorDate",
        "birthDate",
      ];
      // Carimbos reais de data e hora: seguem no fuso local.
      const timestampFields = ["createdAt", "updatedAt"];

      data = data.map((item: any) => {
        dateOnlyFields.forEach((field: any) => {
          if (item[field]) item[field] = displayDateOnly(item[field]);
        });
        timestampFields.forEach((field: any) => {
          if (item[field]) {
            item[field] = new Date(item[field]).toLocaleDateString("pt-BR");
          }
        });
        return item;
      });

      // Troca o nome técnico de cada coluna pelo rótulo em português,
      // montando uma linha nova. A versão anterior renomeava na própria
      // linha (copiava para o rótulo e apagava a chave): quando o rótulo
      // era igual à chave — caso do "CREA" —, a coluna inteira sumia.
      const filteredData = data.map((item: any) => {
        const renamed: any = {};
        Object.keys(item).forEach((key) => {
          renamed[columnLabel(key)] = item[key];
        });
        return renamed;
      });

      const fileName = exportFileName();
      if (exportFormat === "csv") {
        const csv: string = await new Promise((resolve, reject) =>
          jsonExport(filteredData, (err: any, result: any) =>
            err ? reject(err) : resolve(result)
          )
        );
        downloadCSV(csv, fileName);
      } else {
        // Planilha Excel com uma aba
        const ws = XLSX.utils.json_to_sheet(filteredData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sócios");
        XLSX.writeFile(wb, `${fileName}.xlsx`);
      }
      notify(
        filteredData.length === 1
          ? "Planilha gerada com 1 sócio."
          : `Planilha gerada com ${filteredData.length.toLocaleString("pt-BR")} sócios.`,
        { type: "success" }
      );
      return true;
    } catch (error) {
      console.error(error);
      notify("Não foi possível gerar a planilha. Tente de novo.", {
        type: "error",
      });
      return false;
    }
  };

  return (
    <>
      {isMobile ? (
        <Button onClick={handleDialogOpen} size="small">
          <HiOutlineDownload size={18} />
        </Button>
      ) : (
        <Button
          startIcon={<HiOutlineDownload size={18} />}
          onClick={handleDialogOpen}
          size="small"
        >
          Exportar
        </Button>
      )}

      {isDialogOpen && (
        <ExportColumnSelectionDialog
          onClose={handleDialogClose}
          onConfirm={handleColumnSelectionConfirm}
          columns={columns}
          exportFormat="xlsx"
          blockedReason={
            noFilterApplied
              ? "Nenhum filtro aplicado na lista de Sócios. Aplique ao menos um filtro para exportar."
              : undefined
          }
          // selectedColumns from localStorage
          selectedColumns={JSON.parse(
            localStorage.getItem("selectedColumns") || '["id"]'
          )}
        />
      )}
    </>
  );
};
